import styles from "./BalanceBoosterMultiplier.module.css";

import { AviosCurrencyBadge, Badge, Box, Paragraph } from "@alto-avios/alto-ui";
import { formatCurrency } from "@helpers/formatCurrency";
import { useBreakpoint } from "@hooks/useBreakpoint";
import clsx from "clsx";

import { FALLBACK_CURRENCY_CODE } from "./BalanceBoosterMultiplier.constants";
import type { BoostMultiplierCardProps } from "./BalanceBoosterMultiplier.types";

/** Individual boost card showing multiplier, Avios, and price */
export const BoostMultiplierCard = ({
  multiplier,
  aviosAmount,
  price,
  currency,
  locale,
  isSelected,
  showBestValue = false,
  bestValueLabel,
  description,
  onClick,
  disabled = false,
  className,
}: BoostMultiplierCardProps) => {
  const breakpoint = useBreakpoint();
  const hasCurrencyName = breakpoint === "desktop";

  const formattedPrice = formatCurrency({
    value: price,
    locale,
    currency: currency ?? FALLBACK_CURRENCY_CODE,
    removeDecimalsIfWhole: true,
  });

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === "Enter" || event.key === " ") && !disabled && onClick) {
      event.preventDefault();
      onClick();
    }
  };

  // Description wins; else best-value label when flagged.
  const badgeLabel = description ?? (showBestValue ? bestValueLabel : undefined);

  return (
    <div
      className={clsx(
        styles["boost-card"],
        isSelected ? styles["boost-card--selected"] : styles["boost-card--default"],
        disabled && styles["boost-card--disabled"],
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="radio"
      aria-checked={isSelected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
    >
      {badgeLabel && (
        <Badge
          styleVariant="sale"
          size="sm"
          className={clsx(styles["best-value-badge"], disabled && styles["best-value-badge--disabled"])}
        >
          {badgeLabel}
        </Badge>
      )}

      <Box flexDirection="row" alignItems="center" paddingX="none" paddingY="2xs" justifyContent="spaceBetween">
        <Box alignItems="center" gap="xs">
          <Box flexDirection="column">
            <Paragraph foregroundColor={disabled ? "disabledPrimary" : "accentPrimary"} size="lg" textAlign="start">
              {multiplier} %
            </Paragraph>
            <Paragraph size="xs" textAlign="start" foregroundColor={disabled ? "disabledPrimary" : "secondary"}>
              extra Avios
            </Paragraph>
          </Box>

          <div className={clsx(disabled && styles["avios-badge-wrapper--disabled"])}>
            <AviosCurrencyBadge kind="collect" size="lg" hasCurrencyName={hasCurrencyName}>
              {aviosAmount}
            </AviosCurrencyBadge>
          </div>
        </Box>

        <Box alignItems="center" gap="4xs">
          <Paragraph size="xs" foregroundColor={disabled ? "disabledPrimary" : "primary"}>
            for
          </Paragraph>
          <Paragraph size="md" foregroundColor={disabled ? "disabledPrimary" : "secondary"}>
            {formattedPrice}
          </Paragraph>
        </Box>
      </Box>
    </div>
  );
};
